import { useState, useEffect, useCallback } from "react";
import {
  Building2, Upload, Activity, BarChart3, GitCompare, Zap, Leaf, Bell,
  ChevronRight, ChevronDown, ChevronUp, Home, Briefcase, FileSpreadsheet
} from "lucide-react";
import axios, { API } from "@/utils/api";
import { useT, useDeptName } from "@/contexts/LanguageContext";
import { DEPT_ICONS } from "@/utils/constants";
import DashboardView from "@/components/views/DashboardView";
import DepartmentView from "@/components/views/DepartmentView";
import InventoryUploadView from "@/components/views/InventoryUploadView";
import IoTDataView from "@/components/views/IoTDataView";
import ComparisonView from "@/components/views/ComparisonView";
import ConsolidatedView from "@/components/views/ConsolidatedView";
import EnergyReportView from "@/components/views/EnergyReportView";
import CO2ReportView from "@/components/views/CO2ReportView";
import AlertsView from "@/components/views/AlertsView";
import ResidentialDashboardView from "@/components/views/ResidentialDashboardView";
import ResidentialInventoryView from "@/components/views/ResidentialInventoryView";
import ResidentialIoTView from "@/components/views/ResidentialIoTView";
import ResidentialConsolidatedView from "@/components/views/ResidentialConsolidatedView";
import ResidentialComparisonView from "@/components/views/ResidentialComparisonView";
import ResidentialEnergyReportView from "@/components/views/ResidentialEnergyReportView";
import ResidentialCO2ReportView from "@/components/views/ResidentialCO2ReportView";
import CrossAuditReportView from "@/components/views/CrossAuditReportView";
import EnergyDataUploadView from "@/components/views/EnergyDataUploadView";
import AssetManagerView from "@/components/views/AssetManagerView";
import PipelineNav from "@/components/PipelineNav";
import PipelineStepStubView from "@/components/views/PipelineStepStubView";
import { mergePipelineSteps } from "@/utils/pipelineNav";

const ClientDashboard = ({ client, user, onBack, lang, onChangeLang }) => {
  const t = useT();
  const dn = useDeptName();
  const isResidential = (client.client_type === "residential");
  const [currentView, setCurrentView] = useState(isResidential ? "res_dashboard" : "dashboard");
  const [isCommercialExpanded, setIsCommercialExpanded] = useState(!isResidential);
  const [isResidentialExpanded, setIsResidentialExpanded] = useState(isResidential);
  const [departments, setDepartments] = useState([]);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [consolidated, setConsolidated] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedAssetUuid, setSelectedAssetUuid] = useState(null);

  // Commercial pipeline nav state. `pipelineSteps` is the canonical catalog
  // merged with this asset's status; `pipelineStub` is the step whose detail
  // pane is open for a step that has no view of its own.
  const [pipelineCatalog, setPipelineCatalog] = useState([]);
  const [pipelineStatus, setPipelineStatus] = useState([]);
  const [pipelineStub, setPipelineStub] = useState(null);

  // Residential state
  const [resDepartments, setResDepartments] = useState([]);
  const [resConsolidated, setResConsolidated] = useState([]);
  const [resSummary, setResSummary] = useState(null);

  // Commercial departments are asset-scoped: with a building selected the list
  // is that building's db.asset_departments memberships, not the whole catalog.
  // Without a selection (or before assets load) the client-wide list is correct.
  const fetchDepartments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients/${client.id}/departments`, {
        params: selectedAssetUuid ? { asset_uuid: selectedAssetUuid } : {},
      });
      setDepartments(response.data);
    } catch (error) {
      console.error("Error fetching departments:", error);
    }
  }, [client.id, selectedAssetUuid]);

  // Asset-scoped like the departments read above: with a building selected the
  // department counters, the inventory/IoT totals and the CEP energy rollup all
  // narrow to that building. `selectedAssetUuid` is in the dependency list so
  // switching buildings refetches — without it the counters would keep showing
  // the previously selected building's numbers.
  const fetchConsolidated = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients/${client.id}/consolidated/summary`, {
        params: selectedAssetUuid ? { asset_uuid: selectedAssetUuid } : {},
      });
      setConsolidated(response.data.departments || []);
      setSummary(response.data);
    } catch (error) {
      console.error("Error fetching consolidated:", error);
    }
  }, [client.id, selectedAssetUuid]);

  // The canonical step catalog. This is what makes the nav catalog-driven:
  // order, titles, view keys and step 10's parallel components all come from
  // here, so a step added to backend/pipeline_steps.py appears in the nav with
  // no frontend change. Client-independent, so it needs no asset.
  const fetchPipelineCatalog = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/pipeline/steps`);
      setPipelineCatalog(res.data.steps || []);
    } catch (error) {
      console.error("Error fetching pipeline catalog:", error);
    }
  }, []);

  // Per-asset step status, layered on top of the catalog.
  //
  // The status endpoint REQUIRES an explicit asset_uuid — resolve_asset_uuid
  // will not guess a building — so this is skipped until one is selected, and a
  // failure clears status rather than the nav. The nav must still render its
  // full sequence from the catalog when status is unavailable; a nav that
  // blanks out on a failed status call would look like the pipeline vanished.
  const fetchPipelineStatus = useCallback(async () => {
    if (!selectedAssetUuid) {
      setPipelineStatus([]);
      return;
    }
    try {
      const res = await axios.get(`${API}/pipeline/${client.id}/steps`, {
        params: { asset_uuid: selectedAssetUuid },
      });
      setPipelineStatus(res.data.steps || []);
    } catch (error) {
      console.error("Error fetching pipeline status:", error);
      setPipelineStatus([]);
    }
  }, [client.id, selectedAssetUuid]);

  const fetchAlerts = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${client.id}/alerts`);
      setAlerts(res.data.alerts || []);
    } catch (error) {
      console.error("Error fetching alerts:", error);
    }
  }, [client.id]);

  const fetchResDepartments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients/${client.id}/residential/departments`);
      setResDepartments(response.data);
    } catch (error) {
      console.error("Error fetching residential departments:", error);
    }
  }, [client.id]);

  const fetchResConsolidated = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/clients/${client.id}/residential/consolidated/summary`);
      setResConsolidated(response.data.departments || []);
      setResSummary(response.data);
    } catch (error) {
      console.error("Error fetching residential consolidated:", error);
    }
  }, [client.id]);

  const fetchAssets = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/clients/${client.id}/assets`);
      setAssets(res.data);
      if (!selectedAssetUuid && res.data.length > 0) {
        setSelectedAssetUuid(res.data[0].asset_uuid);
      }
    } catch (err) {
      console.error("Error fetching assets:", err);
    }
  }, [client.id, selectedAssetUuid]);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchDepartments(), fetchConsolidated(), fetchAlerts(),
        fetchResDepartments(), fetchResConsolidated(), fetchAssets(),
        fetchPipelineCatalog(), fetchPipelineStatus(),
      ]);
      setLoading(false);
    };
    init();
  }, [fetchDepartments, fetchConsolidated, fetchAlerts, fetchResDepartments, fetchResConsolidated, fetchAssets, fetchPipelineCatalog, fetchPipelineStatus]);

  const handleDepartmentClick = (dept) => {
    setSelectedDepartment(dept);
    setCurrentView("department");
  };

  const handleResDepartmentClick = (dept) => {
    setSelectedDepartment(dept);
    setCurrentView("res_department");
  };

  const handleBackToDashboard = () => {
    setSelectedDepartment(null);
    setCurrentView("dashboard");
    fetchDepartments();
    fetchConsolidated();
  };

  const handleBackToResDashboard = () => {
    setSelectedDepartment(null);
    setCurrentView("res_dashboard");
    fetchResDepartments();
    fetchResConsolidated();
  };

  const handleToggleNA = async (deptKey, notApplicable) => {
    try {
      await axios.put(`${API}/clients/${client.id}/departments/${deptKey}/applicable`, { not_applicable: notApplicable });
      await fetchDepartments();
      await fetchConsolidated();
    } catch (error) {
      console.error("Error toggling N/A:", error);
    }
  };

  const handleResToggleNA = async (deptKey, notApplicable) => {
    try {
      await axios.put(`${API}/clients/${client.id}/residential/departments/${deptKey}/applicable`, { not_applicable: notApplicable });
      await fetchResDepartments();
      await fetchResConsolidated();
    } catch (error) {
      console.error("Error toggling residential N/A:", error);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  // Audit progress counts THIS client's track only. It used to sum the
  // commercial and residential summaries unconditionally, so a commercial
  // client was measured against 6 commercial + 5 residential departments and
  // showed a denominator of 11 for departments it will never audit.
  //
  // For commercial the numbers come from `departments`, which is already
  // narrowed to the selected asset's memberships by the backend. That makes the
  // denominator "distinct applicable departments in this asset" and keeps the
  // numerator over the SAME set — a client-wide completed count over a
  // per-asset denominator could read "4 / 3". `status` and `not_applicable`
  // here are the same department_audits fields consolidated/summary derives
  // audit_status from, so the two agree.
  //
  // Departments remain ONE audit per (client, department): a department present
  // in two buildings is one questionnaire, counted once, never per membership.
  const selectedAssetName =
    assets.find((a) => a.asset_uuid === selectedAssetUuid)?.building_name || null;

  // Catalog + this asset's status. The catalog alone is enough to render the
  // sequence; status only decorates it.
  const pipelineSteps = mergePipelineSteps(pipelineCatalog, pipelineStatus);

  // Soft gating (decision 3): every row navigates regardless of status. A step
  // with a view opens it; a step without one opens the generic stub pane, which
  // states that the step is not built rather than leaving a dead click target.
  const handlePipelineSelect = (target, parentStep = null) => {
    setSelectedDepartment(null);
    if (target.view_key) {
      setPipelineStub(null);
      setCurrentView(target.view_key);
      return;
    }
    setPipelineStub({ step: target, parentStep });
    setCurrentView("pipeline_step");
  };

  const commercialApplicable = departments.filter((d) => !d.not_applicable);
  const commercialCounts = {
    applicable: commercialApplicable.length,
    completed: commercialApplicable.filter((d) => d.status === "completed").length,
    na: departments.filter((d) => d.not_applicable).length,
  };

  const totalApplicable = isResidential
    ? (resSummary?.applicable_departments || 0)
    : commercialCounts.applicable;
  const totalCompleted = isResidential
    ? (resSummary?.audits_completed || 0)
    : commercialCounts.completed;
  const totalNA = isResidential
    ? (resSummary?.not_applicable_departments || 0)
    : commercialCounts.na;

  return (
    <div className="app-container" data-testid="client-dashboard">
      <aside className="sidebar">
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <img src="/logo.png" alt="CO2e Portal" className="sidebar-logo-img" />
          </div>
          <div className="lang-switcher" data-testid="lang-switcher-sidebar">
            {["en","fr","es"].map(l => (
              <button key={l} className={`lang-btn ${lang===l?"active":""}`} onClick={()=>onChangeLang(l)} data-testid={`lang-sidebar-${l}`}>{l.toUpperCase()}</button>
            ))}
          </div>
        </div>

        <div className="audit-context">
          <div className="audit-context-company">
            <Building2 size={13} />
            <span>{client.company_name}</span>
          </div>
          {assets.length > 1 && (
            <div className="asset-selector" data-testid="asset-selector">
              <select
                className="form-input asset-select"
                value={selectedAssetUuid || ""}
                onChange={(e) => setSelectedAssetUuid(e.target.value)}
                data-testid="asset-select-dropdown"
              >
                {assets.map((a) => (
                  <option key={a.asset_uuid} value={a.asset_uuid}>{a.building_name}</option>
                ))}
              </select>
            </div>
          )}
          <div className="audit-context-progress">
            <div className="audit-context-label">{t("audit_progress")}</div>
            <div className="audit-context-stats">
              <span className="audit-completed">{totalCompleted}</span>
              <span className="audit-separator">/</span>
              <span className="audit-total">{totalApplicable} {t("depts")}</span>
            </div>
            <div className="progress-bar" style={{ height: "3px", marginTop: "2px" }}>
              <div
                className="progress-fill success"
                style={{ width: `${totalApplicable > 0 ? (totalCompleted / totalApplicable) * 100 : 0}%` }}
              ></div>
            </div>
            {totalNA > 0 && (
              <div className="text-muted" style={{ fontSize: "0.6rem", marginTop: "1px" }}>
                {totalNA} dept{totalNA > 1 ? "s" : ""} N/A
              </div>
            )}
          </div>
        </div>

        <nav className="sidebar-nav">
          {/* ---- COMMERCIAL AUDITS ---- */}
          <div className="nav-section-title nav-section-toggle" onClick={() => setIsCommercialExpanded(!isCommercialExpanded)} data-testid="toggle-commercial-audits">
            <span className="nav-section-title-left">
              <Briefcase size={14} />
              {t("commercial_audits")}
            </span>
            {isCommercialExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </div>
          {/* The 10-step pipeline, rendered from the canonical catalog. No step
              list lives here: see backend/pipeline_steps.py. The Energy
              Efficiency Report used to sit in this group — it is NOT a pipeline
              step (decision 7) and now lives in MAIN below. */}
          <div className={`nav-section-items ${isCommercialExpanded ? "expanded" : "collapsed"}`} data-testid="commercial-audits-items">
            <PipelineNav
              steps={pipelineSteps}
              currentView={selectedDepartment ? "department" : currentView}
              stubStepKey={pipelineStub?.step?.key}
              onSelect={handlePipelineSelect}
            />
          </div>

          {/* ---- RESIDENTIAL AUDITS ---- */}
          <div className="nav-section-title nav-section-toggle" onClick={() => setIsResidentialExpanded(!isResidentialExpanded)} data-testid="toggle-residential-audits" style={{ marginTop: "8px" }}>
            <span className="nav-section-title-left">
              <Home size={14} />
              {t("residential_audits")}
            </span>
            {isResidentialExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </div>
          <div className={`nav-section-items ${isResidentialExpanded ? "expanded" : "collapsed"}`} data-testid="residential-audits-items">
            <div className={`nav-item ${currentView === "res_dashboard" && !selectedDepartment ? "active" : ""}`} onClick={() => { setCurrentView("res_dashboard"); setSelectedDepartment(null); }} data-testid="nav-residential-dashboard">
              <span className="nav-item-icon"><BarChart3 size={20} /></span>{t("dashboard")}
            </div>
            <div className={`nav-item ${currentView === "res_inventory" ? "active" : ""}`} onClick={() => { setCurrentView("res_inventory"); setSelectedDepartment(null); }} data-testid="nav-residential-inventory">
              <span className="nav-item-icon"><Upload size={20} /></span>{t("inventory_upload")}
            </div>
            <div className={`nav-item ${currentView === "res_energy_data" ? "active" : ""}`} onClick={() => { setCurrentView("res_energy_data"); setSelectedDepartment(null); }} data-testid="nav-residential-energy-data">
              <span className="nav-item-icon"><FileSpreadsheet size={20} /></span>{t("energy_data_upload")}
            </div>
            <div className={`nav-item ${currentView === "res_iot" ? "active" : ""}`} onClick={() => { setCurrentView("res_iot"); setSelectedDepartment(null); }} data-testid="nav-residential-iot">
              <span className="nav-item-icon"><Activity size={20} /></span>{t("iot_data")}
            </div>
            <div className={`nav-item ${currentView === "res_comparison" ? "active" : ""}`} onClick={() => { setCurrentView("res_comparison"); setSelectedDepartment(null); }} data-testid="nav-residential-comparison">
              <span className="nav-item-icon"><GitCompare size={20} /></span>{t("data_comparison")}
            </div>
            <div className={`nav-item ${currentView === "res_consolidated" ? "active" : ""}`} onClick={() => { setCurrentView("res_consolidated"); setSelectedDepartment(null); }} data-testid="nav-residential-consolidated">
              <span className="nav-item-icon"><BarChart3 size={20} /></span>{t("consolidated_results")}
            </div>
            <div className={`nav-item ${currentView === "res_energy_report" ? "active" : ""}`} onClick={() => { setCurrentView("res_energy_report"); setSelectedDepartment(null); }} data-testid="nav-residential-energy-report">
              <span className="nav-item-icon"><Zap size={20} /></span>{t("energy_report")}
            </div>
            <div className={`nav-item ${currentView === "res_co2_report" ? "active" : ""}`} onClick={() => { setCurrentView("res_co2_report"); setSelectedDepartment(null); }} data-testid="nav-residential-co2-report">
              <span className="nav-item-icon"><Leaf size={20} /></span>{t("co2_report")}
            </div>
          </div>

          {/* ---- CROSS-AUDIT & TOOLS ---- */}
          {/* Standalone tools, not pipeline steps. */}
          <div className="nav-section-title" style={{ marginTop: "8px" }}>{t("main")}</div>
          {/* Energy Efficiency Report: relocated here from the Commercial
              Audits group (decision 7). It is NOT EEVC, and sitting inside the
              pipeline group is what caused the two to be confused. The view,
              its currentView key and its data-testid are unchanged — the
              testid keeps its original name deliberately, because the testing
              agent keys on it. */}
          <div className={`nav-item ${currentView === "energy_report" ? "active" : ""}`} onClick={() => { setCurrentView("energy_report"); setSelectedDepartment(null); }} data-testid="nav-commercial-energy-report">
            <span className="nav-item-icon"><Zap size={20} /></span>{t("energy_report")}
          </div>
          <div className={`nav-item ${currentView === "cross_audit" ? "active" : ""}`} onClick={() => { setCurrentView("cross_audit"); setSelectedDepartment(null); }} data-testid="nav-cross-audit">
            <span className="nav-item-icon"><GitCompare size={20} /></span>Cross-Audit Report
          </div>
          <div className={`nav-item ${currentView === "alerts" ? "active" : ""}`} onClick={() => { setCurrentView("alerts"); setSelectedDepartment(null); fetchAlerts(); }} data-testid="nav-alerts">
            <span className="nav-item-icon"><Bell size={20} /></span>{t("alerts")}
            {alerts.length > 0 && <span className="alert-count-badge">{alerts.length}</span>}
          </div>

          {/* ---- NAVIGATION ---- */}
          <div className="nav-section-title">{t("navigation")}</div>
          <div className={`nav-item ${currentView === "assets" ? "active" : ""}`} onClick={() => { setCurrentView("assets"); setSelectedDepartment(null); }} data-testid="nav-assets">
            <span className="nav-item-icon"><Building2 size={20} /></span>{t("assets_title")}
          </div>
          <div className="nav-item" onClick={onBack} data-testid="nav-back-clients">
            <span className="nav-item-icon"><ChevronRight size={20} style={{ transform: "rotate(180deg)" }} /></span>
            {t("back_to_clients")}
          </div>
        </nav>
      </aside>

      <main className="main-content">
        {/* Commercial views */}
        {currentView === "dashboard" && !selectedDepartment && (
          <DashboardView departments={departments} onDepartmentClick={handleDepartmentClick} onToggleNA={handleToggleNA} clientId={client.id} selectedAssetName={selectedAssetName} />
        )}
        {currentView === "department" && selectedDepartment && (
          <DepartmentView clientId={client.id} department={selectedDepartment} onBack={handleBackToDashboard} />
        )}
        {currentView === "inventory" && (
          <InventoryUploadView clientId={client.id} assetUuid={selectedAssetUuid} />
        )}
        {currentView === "energy_data" && (
          <EnergyDataUploadView clientId={client.id} assetUuid={selectedAssetUuid} />
        )}
        {currentView === "iot" && (
          <IoTDataView clientId={client.id} assetUuid={selectedAssetUuid} />
        )}
        {currentView === "consolidated" && (
          <ConsolidatedView clientId={client.id} consolidated={consolidated} summary={summary} onRefresh={fetchConsolidated} />
        )}
        {/* Detail pane for a pipeline step with no view of its own. One generic
            component for all of them — it builds none of the missing steps. */}
        {currentView === "pipeline_step" && pipelineStub && (
          <PipelineStepStubView
            step={pipelineStub.step}
            parentStep={pipelineStub.parentStep}
            steps={pipelineSteps}
            onBack={() => { setPipelineStub(null); setCurrentView("dashboard"); }}
          />
        )}

        {/* Residential views */}
        {currentView === "res_dashboard" && !selectedDepartment && (
          <ResidentialDashboardView departments={resDepartments} onDepartmentClick={handleResDepartmentClick} onToggleNA={handleResToggleNA} clientId={client.id} />
        )}
        {currentView === "res_department" && selectedDepartment && (
          <DepartmentView clientId={client.id} department={selectedDepartment} onBack={handleBackToResDashboard} auditType="residential" />
        )}
        {currentView === "res_inventory" && (
          <ResidentialInventoryView clientId={client.id} />
        )}
        {currentView === "res_energy_data" && (
          <EnergyDataUploadView clientId={client.id} assetUuid={selectedAssetUuid} />
        )}
        {currentView === "res_iot" && (
          <ResidentialIoTView clientId={client.id} />
        )}
        {currentView === "res_comparison" && (
          <ResidentialComparisonView clientId={client.id} />
        )}
        {currentView === "res_consolidated" && (
          <ResidentialConsolidatedView clientId={client.id} consolidated={resConsolidated} summary={resSummary} onRefresh={fetchResConsolidated} />
        )}
        {currentView === "res_energy_report" && (
          <ResidentialEnergyReportView clientId={client.id} />
        )}
        {currentView === "res_co2_report" && (
          <ResidentialCO2ReportView clientId={client.id} />
        )}

        {/* Shared views */}
        {currentView === "comparison" && (
          <ComparisonView clientId={client.id} assetUuid={selectedAssetUuid} />
        )}
        {currentView === "energy_report" && (
          <EnergyReportView clientId={client.id} />
        )}
        {currentView === "co2_report" && (
          <CO2ReportView clientId={client.id} />
        )}
        {currentView === "cross_audit" && (
          <CrossAuditReportView clientId={client.id} />
        )}
        {currentView === "alerts" && (
          <AlertsView clientId={client.id} alerts={alerts} onRefresh={fetchAlerts} />
        )}
        {currentView === "assets" && (
          <AssetManagerView clientId={client.id} onAssetChange={fetchAssets} />
        )}
      </main>
    </div>
  );
};

export default ClientDashboard;
